import {
  ArrowAliasV2,
  DataSet,
  DataSetView,
} from '../../interfaces/arrow-alias.interface';
import { GeneralHelpers } from '../../helpers/general.helper';
import { RecordBatchReader, Schema, Table, tableToIPC } from 'apache-arrow';
import { PutStreamMachine } from './state-machine/put-stream';
import { ArrowError } from './arrow-error';
import { TypedParam } from './query-params';
import { signal } from '@angular/core';

const DEFAULT_BATCH_SIZE = 9000;
const MAX_BATCH_SIZE = 250000;

/**
 * Hard timeout for Arrow Flight request/response operations. A lost or
 * unparseable server response can never hang a workflow step forever; the
 * pending promise rejects once this elapses. Genuine query errors surface
 * much faster via the normal Error-message path, so this only backstops the
 * pathological "no usable response ever arrives" case and can be generous.
 */
const REQUEST_TIMEOUT_MS = 600000; // 10 minutes

export type ResponseHeader = {
  message: string;
  trans_id: number;
};

export type CreateTableParams = {
  name: string;
  connection_name: string;
  path: string;
  src_connection_name?: string;
  src_path?: string;
  connection_id?: string;
};

enum ArrowClientState {
  DISCONNECTED,
  AWAIT_CONNECT,
  AWAIT_DS_LIST,
  OPEN,
}

type ReadInfo = {
  buffer: Buffer;
  promise: Promise<Uint8Array>;
};

type ReadCallbacks = {
  queryFailed: (reason: any) => void;
  querySuccess: (data: Uint8Array) => void;
  asTable: boolean;
  rawBytes?: boolean;
};

export class ArrowClient {
  readonly #ws!: WebSocket;
  #state = ArrowClientState.DISCONNECTED;
  #transId = 1;
  #disconnecting = false;
  #currentPut: PutStreamMachine | undefined = undefined;
  #resolveConnected = () => { };
  readonly #pending = new Map<number, (message: any) => void>();
  readonly #dataSets = signal(new Map<string, any>());
  /** signal with the datasets for this connection keyed by name */
  dataSets = this.#dataSets.asReadonly();
  readonly #views = new Map<string, any>();
  readonly #reads = new Map<number, ReadInfo>();
  readonly #completeReads = new Map<number, ReadCallbacks>();
  readonly #readySignal = signal<boolean>(false);
  /** signal that the client is ready to use */
  ready = this.#readySignal.asReadonly();

  // callback to be called when the websocket is closed
  wsClosedCallback = () => { };

  constructor(public alias: ArrowAliasV2, public readonly url: string) {
    try {
      this.#ws = new WebSocket(url);
    } catch (e) {
      console.error('ArrowClient: Failed to create WebSocket', e);
    }
  }

  /**
   * Returns true if the client is connected to the server
   *
   * @readonly
   */
  get connected() {
    return (
      this.#state === ArrowClientState.OPEN &&
      this.#ws.readyState === WebSocket.OPEN
    );
  }

  async connect(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.#resolveConnected = resolve;
      this.#ws.binaryType = 'arraybuffer';
      this.#ws.onopen = () => {
        this.#ws.send(
          JSON.stringify({
            message: 'Connect',
            url: this.alias.url,
            trans_id: this.#transId++,
          })
        );
      };
      this.#ws.onmessage = (event) => {
        this.#handleMessage(event);
      };
      this.#ws.onclose = () => {
        this.wsClosedCallback();
        this.#state = ArrowClientState.DISCONNECTED;
        this.#readySignal.set(false);
        if (!this.#disconnecting) {
          reject(new Error('ArrowClient: Connection closed unexpectedly'));
        }
      };
    });
  }

  async disconnect(): Promise<void> {
    const msg = { message: 'Disconnect', trans_id: this.#transId++ };
    this.#disconnecting = true;
    this.#ws.send(JSON.stringify(msg));
  }

  listDataSets(): DataSet[] {
    try {
      const dataSetsMap = this.#dataSets();
      if (!dataSetsMap) {
        return [];
      }
      return Array.from(dataSetsMap.values()).filter((dataset): dataset is DataSet => dataset !== null && dataset !== undefined);
    } catch (error) {
      console.error('Error listing datasets:', error);
      return [];
    }
  }

  refreshDataSetList(): Promise<DataSet[]> {
    return new Promise<DataSet[]>((resolve, reject) => {
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message.message === 'Error') {
          reject(
            new ArrowError(
              `ArrowClient: Error refreshing DataSets: ${message.description}`,
              message
            )
          );
          return;
        }
        this.#handleDataSetList(message);
        resolve(this.listDataSets());
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'DSList',
          argument: '',
          trans_id: tid,
        })
      );
    });
  }

  createDataSet(
    dataSetName: string,
    base_path: string,
    partition_cols: any[],
    batchSize: number,
    connectionName: string
  ): Promise<void> {
    if (!connectionName) {
      return Promise.reject(new Error('ArrowClient: connectionName is required'));
    }
    batchSize = this.#massageBatchSize(batchSize);
    return new Promise<void>((resolve, reject) => {
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message.message === 'Error') {
          reject(
            new ArrowError(
              `ArrowClient: Error creating DataSet: ${message.description}`,
              message
            )
          );
          return;
        }
        const ds = JSON.parse(message.results);
        ds.partitionCols = JSON.parse(ds.partition_cols);
        this.#dataSets.update((map) => {
          const next = new Map(map);
          next.set(dataSetName, ds);
          return next;
        });
        resolve();
      });
      const argStr = JSON.stringify({
        name: dataSetName,
        base_path,
        partition_cols,
        connection_name: connectionName,
        batch_size: batchSize,
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'DSCreate',
          argument: argStr,
          trans_id: tid,
        })
      );
    });
  }

  deleteDataSet(dataSetName: string) {
    return new Promise<void>((resolve, reject) => {
      const dataSet = this.#dataSets().get(dataSetName);
      if (!dataSet) {
        reject(new Error(`ArrowClient: DataSet ${dataSetName} not found`));
        return;
      }
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message.message === 'Error') {
          reject(
            new ArrowError(
              `ArrowClient: Error deleting DataSet: ${message.description}`,
              message
            )
          );
          return;
        }
        this.#dataSets.update((map) => {
          const next = new Map(map);
          next.delete(dataSetName);
          return next;
        });
        resolve();
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'DSDelete',
          argument: `${dataSetName}`,
          trans_id: tid,
        })
      );
    });
  }

  createView(
    dataSetName: string,
    viewName: string,
    expiresAfter: number,
    partitionRegex: string,
    query?: string
  ) {
    return new Promise<string>((resolve, reject) => {
      const dataSet = this.#dataSets().get(dataSetName);
      if (!dataSet) {
        reject(new Error(`ArrowClient: DataSet ${dataSetName} not found`));
        return;
      }
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message.message === 'Error') {
          reject(
            new ArrowError(
              `ArrowClient: Error creating View '${viewName}': ${message.description}`,
              message
            )
          );
          return;
        }
        const view = JSON.parse(message.results);
        let views = this.#views.get(dataSetName);
        if (!views) {
          views = [view];
          this.#views.set(dataSetName, views);
        }
        views.push(view);
        resolve(view);
      });
      const argStr = JSON.stringify({
        dataset_id: dataSet.dataset_id,
        name: viewName,
        expires_after: expiresAfter,
        partition_regex: partitionRegex,
        query,
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'DSView',
          argument: argStr,
          trans_id: tid,
        })
      );
    });
  }

  deleteView(dataSetName: string, viewName: string) {
    return new Promise<void>((resolve, reject) => {
      const dataSet = this.#dataSets().get(dataSetName);
      if (!dataSet) {
        reject(new Error(`ArrowClient: DataSet ${dataSetName} not found`));
        return;
      }
      const views = this.#views.get(dataSetName);
      if (!views) {
        reject(
          new Error(
            `ArrowClient: Views for DataSet ${dataSetName} not found. Did you forget to list them?`
          )
        );
        return;
      }
      const view = views.find((v: any) => v.name === viewName);
      if (!view) {
        reject(
          new Error(
            `ArrowClient: View ${viewName} not found in DataSet ${dataSetName}`
          )
        );
        return;
      }
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message.message === 'Error') {
          reject(
            new ArrowError(
              `ArrowClient: Error deleting View: ${message.description}`,
              message
            )
          );
          return;
        }
        views.splice(views.indexOf(view), 1);
        this.#views.set(dataSetName, views);
        resolve();
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'DSViewDelete',
          argument: view.view_id.toString(),
          trans_id: tid,
        })
      );
    });
  }

  /// send a user-specified action to the server and return the results
  doAction(action: string, argument: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message.message === 'Error') {
          reject(
            new ArrowError(`ArrowClient: Error doing action: ${message.description}`, message)
          );
          return;
        }
        resolve(message.results);
      });
      this.#sendMessage({ message: 'DoAction', action, argument, trans_id: tid }, tid);
    });
  }

  initCache(dataSetName: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!dataSetName) {
        reject(new Error('ArrowClient: No dataSetName provided'));
        return;
      }
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message?.message === 'Error') {
          reject(new ArrowError(`ArrowClient: Error initializing cache: ${message.description}`, message));
          return;
        }
        resolve();
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'DSCache',
          argument: dataSetName,
          trans_id: tid,
        })
      );
    });
  }

  /** Returns undefined if listViews() was not previously called. */
  getCachedViews(dataSetName: string): DataSetView[] {
    return this.#views.get(dataSetName) || [];
  }

  listViews(dataSetName: string) {
    return new Promise<DataSetView[]>((resolve, reject) => {
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message.message === 'Error') {
          reject(
            new ArrowError(
              `ArrowClient: Error listing views: ${message.description}`,
              message
            )
          );
          return;
        }
        const views = JSON.parse(message.results);
        this.#views.set(dataSetName, views);
        resolve(views);
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'DSViewList',
          argument: dataSetName,
          trans_id: tid,
        })
      );
    });
  }

  getSchema(dataSetName: string) {
    return new Promise<object>((resolve, reject) => {
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message?.message === 'Error') {
          reject(new ArrowError(`ArrowClient: Error getting schema: ${message.description}`, message));
          return;
        }
        const data = JSON.parse(message.results);
        resolve(data);
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'DSGetSchema',
          argument: dataSetName,
          trans_id: tid,
        })
      );
    });
  }

  registerSchema(datasetName: string, schemaFields: object[]): Promise<object> {
    const payload = JSON.stringify({ dataset_name: datasetName, schema_fields: schemaFields });
    return this.doAction('RegisterSchema', payload).then((result) => JSON.parse(result));
  }

  listCloud(connectionName: string, prefix: string = '', delimiter?: string, max_keys?: number): Promise<string[]> {
    const args = { connection_name: connectionName, prefix, delimiter, max_keys };
    return new Promise<string[]>((resolve, reject) => {
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message.message === 'Error') {
          reject(new ArrowError(message.description || 'ListCloud failed', message));
          return;
        }
        const data = JSON.parse(message.results);
        resolve(data);
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'ListCloud',
          argument: JSON.stringify(args),
          trans_id: tid,
        })
      );
    });
  }

  listCloudDirs(connectionName: string, prefix?: string): Promise<string[]> {
    const args = { connection_name: connectionName, prefix: prefix ?? '' };
    return new Promise<string[]>((resolve, reject) => {
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message.message === 'Error') {
          reject(new ArrowError(message.description || 'ListCloudDirs failed', message));
          return;
        }
        const data = JSON.parse(message.results);
        resolve(data);
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'ListCloudDirs',
          argument: JSON.stringify(args),
          trans_id: tid,
        })
      );
    });
  }

  cloudBasePathExists(connectionName: string, basePath: string): Promise<boolean> {
    const args = { connection_name: connectionName, prefix: basePath, delimiter: '/' };
    return new Promise<boolean>((resolve, reject) => {
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message.message === 'Error') {
          reject(new ArrowError(message.description || 'ListCloud failed', message));
          return;
        }
        const data = JSON.parse(message.results);
        const prefixes = data.prefixes || [];
        const objects = data.objects || [];
        resolve(prefixes.length > 0 || objects.length > 0);
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'ListCloud',
          argument: JSON.stringify(args),
          trans_id: tid,
        })
      );
    });
  }

  /**
   * @param dataSetName The name of the dataset to query
   * @param query The query to run against the view and or any static tables
   * @param asTable If true, the actual arrow table will be returned. otherwise, a binary representation of the table will be returned
   * @param params Typed positional values bound to the query's `$1`, `$2`, ... placeholders (EHQ-115)
   * @returns an Arrow table in table format or IPC format
   */
  performQuery(
    dataSetName: string | undefined,
    query: string,
    asTable: boolean = false,
    params?: TypedParam[]
  ): Promise<Uint8Array> {
    if (!this.#ws || this.#ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('ArrowClient: WebSocket not connected'));
    }
    // Restored in EHQ-115. SCRUM-50 removed dataSetName as "vestigial" because
    // every *Angular* caller passed undefined, but the dynamic step-code path
    // reaches this through args marshalled at runtime, where no static search
    // could see the callers. The result was that a documented, headless-badged
    // parameter was silently ignored in the browser: flight_id stayed 0, so no
    // dataset view was ever registered under its unqualified name and a step
    // using the documented dataset form failed in the browser while succeeding
    // headless. enoch is the definitive source for this signature.
    let flight_id = 0;
    if (dataSetName) {
      const dataSet = this.#dataSets().get(dataSetName);
      if (dataSet) {
        flight_id = dataSet.dataset_id;
      }
    }

    const tid = this.#transId++;
    const info = {
      buffer: Buffer.alloc(0),
      promise: new Promise<Uint8Array>((resolve, reject) => {
        this.#completeReads.set(tid, {
          querySuccess: resolve,
          queryFailed: reject,
          asTable,
        });
      }),
    };
    this.#reads.set(tid, info);
    this.#pending.set(tid, (data) => {
      if (data.message === 'Error') {
        // there was an error reading the table
        const comp = this.#completeReads.get(tid);
        if (comp) {
          comp.queryFailed(
            new ArrowError(`Error performing query: ${data.description}`, data)
          );
          this.#completeReads.delete(tid);
        }
        this.#reads.delete(tid);
      } else if (data.message === 'Success' && this.#completeReads.get(tid)) {
        // success w/o data means the resulting table is empty
        this.#completeReads.get(tid)?.queryFailed(new Error('No data found'));
        this.#completeReads.delete(tid);
      }
    });
    // create the ticket. `params` is omitted entirely when absent, so an
    // unparameterized query produces exactly the ticket it always has
    // (EHQ-115). The legacy `query_params` string map is gone: it was never
    // reachable from step code and newton rejects a ticket carrying both.
    const ticket = JSON.stringify({
      flight_id,
      query,
      path: dataSetName,
      first_batch_only: false,
      ...(params ? { params } : {}),
    });
    this.#ws.send(JSON.stringify({ message: 'Read', trans_id: tid, ticket }));
    return this.#withTimeout(tid, info.promise);
  }

  /**
   *
   * @param query The query to run against views and or static tables
   * @param tableName The name of the table to save the results as. If the table already exists, an error will be returned
   * @returns The table definition
   */
  saveQueryAsTable(
    query: string,
    tableName: string,
    params?: TypedParam[]
  ): Promise<void> {
    if (!this.#ws || this.#ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('ArrowClient: WebSocket not connected'));
    }
    const tid = this.#transId++;
    const promise = new Promise<void>((resolve, reject) => {
      this.#pending.set(tid, (data) => {
        if (data.message === 'Error') {
          reject(new ArrowError(`Error performing sql query: ${data.description}`, data));
        } else {
          resolve(data.results);
        }
      });
      const args = { query, table_name: tableName, ...(params ? { params } : {}) };
      // create the ticket
      const msg = JSON.stringify({
        message: 'DoAction',
        action: 'SaveQuery',
        argument: JSON.stringify(args),
        trans_id: tid,
      });
      this.#ws.send(msg);
    });
    return this.#withTimeout(tid, promise);
  }

  /**
   *
   * @param query The query to run against views and or static tables
   * @param tableName The name of the table to save the results as. If the table already exists, an error will be returned
   * @returns The table definition
   */
  saveQueryToCloud(
    query: string,
    connectionName: string,
    path: string,
    params?: TypedParam[]
  ): Promise<void> {
    if (!this.#ws || this.#ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('ArrowClient: WebSocket not connected'));
    }
    const tid = this.#transId++;
    return new Promise<void>((resolve, reject) => {
      this.#pending.set(tid, (data) => {
        if (data.message === 'Error') {
          reject(new ArrowError(`Error saving query to cloud: ${data.description}`, data));
        } else {
          resolve();
        }
      });
      const args = {
        query,
        connection_name: connectionName,
        bucket_path: path,
        ...(params ? { params } : {}),
      };
      const msg = JSON.stringify({
        message: 'DoAction',
        action: 'SaveQuery',
        argument: JSON.stringify(args),
        trans_id: tid,
      });
      this.#ws.send(msg);
    });
  }

  createPut(
    dataSetName: string,
    base_path: string,
    partition_cols: any[],
    batchSize: number,
    table: Buffer,
    connectionName?: string
  ) {
    if (this.#currentPut) {
      return Promise.reject(new Error('ArrowClient: Put already in progress'));
    }
    return new Promise<number>((resolve, reject) => {
      if (!connectionName) {
        reject(new Error('ArrowClient: connectionName is required for createPut'));
        return;
      }
      batchSize = this.#massageBatchSize(batchSize);
      const tid = this.#transId++;
      const mdStr = JSON.stringify({
        name: dataSetName,
        base_path,
        partition_cols,
        connection_name: connectionName,
        batch_size: batchSize,
      });
      const putMachine = new PutStreamMachine(
        tid,
        undefined,
        mdStr,
        table,
        this.#ws
      );
      this.#currentPut = putMachine;
      // Safety net: a PUT that never receives PutComplete must not hang the run
      // forever. Reject after a hard ceiling instead of looping indefinitely.
      // (The real fix is the 20-byte terminal frame in put-stream.ts; this is defense.)
      const timeoutId = setTimeout(() => {
        if (this.#currentPut === putMachine) {
          this.#currentPut = undefined;
          reject(new Error(`ArrowClient: createPut timed out after ${REQUEST_TIMEOUT_MS}ms (tid=${tid})`));
        }
      }, REQUEST_TIMEOUT_MS);
      this.#currentPut.errorHandler = (reason: any) => {
        clearTimeout(timeoutId);
        reject(new Error(reason));
        this.#currentPut = undefined;
      };
      this.#currentPut.resolveHandler = (putId: number) => {
        clearTimeout(timeoutId);
        const list_tid = this.#transId++;
        this.#pending.set(list_tid, (message) => {
          this.#handleDataSetList(message);
        });
        this.#ws.send(
          JSON.stringify({
            message: 'DoAction',
            action: 'DSList',
            argument: '',
            trans_id: list_tid,
          })
        );
        resolve(putId);
        this.#currentPut = undefined;
      };
      this.#currentPut.start();
    });
  }

  initPut(dataSetName: string) {
    const dataSet = this.#dataSets().get(dataSetName);
    if (!dataSet) {
      return Promise.reject(
        new Error(`ArrowClient: DataSet ${dataSetName} not found`)
      );
    }
    return new Promise<number>((resolve, reject) => {
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message?.message === 'Error') {
          reject(new ArrowError(`ArrowClient: Error initializing put: ${message.description}`, message));
          return;
        }
        const data = JSON.parse(message.results);
        console.log(`got put_id = ${data.put_command.put_id}`);
        resolve(data.put_command.put_id);
      });
      const argStr = JSON.stringify({
        dataset_id: dataSet.dataset_id,
        update_views: true,
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'DSInitPut',
          argument: argStr,
          trans_id: tid,
        })
      );
    });
  }

  continuePut(put_id: number, table: Buffer) {
    if (this.#currentPut) {
      return Promise.reject(new Error('ArrowClient: Put already in progress'));
    }
    return new Promise<void>((resolve, reject) => {
      const tid = this.#transId++;
      const putMachine = new PutStreamMachine(
        tid,
        undefined,
        undefined,
        table,
        this.#ws
      );
      this.#currentPut = putMachine;
      // Safety net (see createPut): never hang forever on a missing PutComplete.
      const timeoutId = setTimeout(() => {
        if (this.#currentPut === putMachine) {
          this.#currentPut = undefined;
          reject(new Error(`ArrowClient: continuePut timed out after ${REQUEST_TIMEOUT_MS}ms (tid=${tid})`));
        }
      }, REQUEST_TIMEOUT_MS);
      this.#currentPut.errorHandler = (reason: any) => {
        clearTimeout(timeoutId);
        reject(new Error(reason));
        this.#currentPut = undefined;
      };
      this.#currentPut.resolveHandler = () => {
        clearTimeout(timeoutId);
        resolve();
        this.#currentPut = undefined;
      };
      this.#currentPut.start(put_id);
    });
  }

  endPut(put_id: number) {
    const argStr = JSON.stringify({ command: 'DSEndPut', put_id });
    return new Promise<void>((resolve, reject) => {
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message.message === 'Error') {
          reject(
            new ArrowError(`ArrowClient: Error ending Put: ${message.description}`, message)
          );
          return;
        }
        resolve();
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'DSEndPut',
          argument: argStr,
          trans_id: tid,
        })
      );
    });
  }

  listTables() {
    return new Promise<object[]>((resolve, reject) => {
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message?.message === 'Error') {
          reject(new ArrowError(`ArrowClient: Error listing tables: ${message.description}`, message));
          return;
        }
        const data = JSON.parse(message.results);
        resolve(data);
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'STList',
          argument: '',
          trans_id: tid,
        })
      );
    });
  }

  createTable(args: CreateTableParams) {
    // Validate WebSocket connection
    if (!this.#ws || this.#ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('ArrowClient: WebSocket not connected'));
    }

    // Validate required parameters before sending to server
    if (!args || typeof args !== 'object') {
      return Promise.reject(new Error('ArrowClient: createTable requires a valid arguments object'));
    }

    if (!args.name || typeof args.name !== 'string' || args.name.trim() === '') {
      return Promise.reject(new Error('ArrowClient: createTable requires a non-empty table name'));
    }

    if (!args.connection_name || typeof args.connection_name !== 'string' || args.connection_name.trim() === '') {
      return Promise.reject(new Error('ArrowClient: createTable requires a non-empty connection_name'));
    }

    if (!args.path || typeof args.path !== 'string' || args.path.trim() === '') {
      return Promise.reject(new Error('ArrowClient: createTable requires a non-empty path'));
    }

    // src_path is optional - only required when converting Arrow IPC to Parquet
    // If src_path is provided, src_connection_name should also be provided
    if (args.src_path && (!args.src_connection_name || args.src_connection_name.trim() === '')) {
      return Promise.reject(new Error('ArrowClient: createTable requires src_connection_name when src_path is provided'));
    }

    args.connection_id = this.alias.aliasId.toString();
    return new Promise<object>((resolve, reject) => {
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message.message === 'Error') {
          reject(
            new ArrowError(
              `ArrowClient: Error creating table: ${message.description}`,
              message
            )
          );
          return;
        }
        const table = JSON.parse(message.results);
        resolve(table);
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'STCreate',
          argument: JSON.stringify(args),
          trans_id: tid,
        })
      );
    });
  }

  uploadTable(tableName: string, table: Buffer) {
    // Validate WebSocket connection
    if (!this.#ws || this.#ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('ArrowClient: WebSocket not connected'));
    }
    
    // Validate inputs before proceeding
    if (!tableName || typeof tableName !== 'string' || tableName.trim() === '') {
      return Promise.reject(new Error('ArrowClient: uploadTable requires a non-empty table name'));
    }
    
    if (!table || !Buffer.isBuffer(table) || table.length === 0) {
      return Promise.reject(new Error('ArrowClient: uploadTable requires a non-empty Buffer containing Arrow IPC data'));
    }
    
    if (this.#currentPut) {
      return Promise.reject(new Error('ArrowClient: Put already in progress'));
    }
    
    return new Promise<void>((resolve, reject) => {
      const tid = this.#transId++;
      
      // Set up timeout to prevent indefinite hanging (60 seconds default)
      const timeoutDuration = 60000; // 60 seconds
      const timeoutId = setTimeout(() => {
        if (this.#currentPut && this.#currentPut.transId === tid) {
          this.#currentPut = undefined;
          reject(new Error(`ArrowClient: uploadTable timed out after ${timeoutDuration}ms. The server may not be responding.`));
        }
      }, timeoutDuration);
      
      this.#currentPut = new PutStreamMachine(
        tid,
        tableName,
        undefined,
        table,
        this.#ws
      );
      this.#currentPut.errorHandler = (reason: any) => {
        clearTimeout(timeoutId);
        this.#currentPut = undefined;
        reject(new Error(reason));
      };
      this.#currentPut.resolveHandler = () => {
        clearTimeout(timeoutId);
        this.#currentPut = undefined;
        console.log('table uploaded');
        resolve();
      };
      this.#currentPut.start();
    });
  }

  updateTables() {
    return new Promise<void>((resolve, reject) => {
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message.message === 'Error') {
          reject(
            new ArrowError(
              `ArrowClient: Error updating tables: ${message.description}`,
              message
            )
          );
          return;
        }
        resolve();
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'STUpdate',
          argument: '',
          trans_id: tid,
        })
      );
    });
  }

  deleteTable(name: string) {
    return new Promise<void>((resolve, reject) => {
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message.message === 'Error') {
          reject(
            new ArrowError(
              `ArrowClient: Error deleting table: ${message.description}`,
              message
            )
          );
          return;
        }
        resolve();
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'STDelete',
          argument: name,
          trans_id: tid,
        })
      );
    });
  }

  // DEAD CODE (EHQ-5): the SignResource DoAction presign path is no longer
  // reached — presign is served over HTTP via ObjectStorageFileService. Left in
  // place intentionally; the newton-rs path is not removed in this change.
  presignUrl(connectionName: string, path: string, duration: number | undefined) {
    return new Promise<string>((resolve, reject) => {
      const tid = this.#transId++;
      this.#pending.set(tid, (message) => {
        if (message?.message === 'Error') {
          reject(new ArrowError(`ArrowClient: Error presigning URL: ${message.description}`, message));
          return;
        }
        resolve(message.results);
      });
      const argStr = JSON.stringify({ connection_name: connectionName, path, duration });
      this.#sendMessage(
        {
          message: 'DoAction',
          action: 'SignResource',
          argument: argStr,
          trans_id: tid,
        },
        tid
      );
    });
  }

  /**
   * Execute a SQL query against a database connection by name.
   * Sends the ReadDB WebSocket operation.
   *
   * @param connectionName The name of the Database connection
   * @param query The SQL query to execute
   * @returns Arrow IPC binary data via the pending/reads mechanism
   */
  performDbQuery(
    connectionName: string,
    query: string,
    params?: TypedParam[]
  ): Promise<Uint8Array> {
    if (!this.#ws || this.#ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('ArrowClient: WebSocket not connected'));
    }
    const tid = this.#transId++;
    const info = {
      buffer: Buffer.alloc(0),
      promise: new Promise<Uint8Array>((resolve, reject) => {
        this.#completeReads.set(tid, {
          querySuccess: resolve,
          queryFailed: reject,
          asTable: false,
          rawBytes: true,
        });
      }),
    };
    this.#reads.set(tid, info);
    this.#pending.set(tid, (data) => {
      if (data.message === 'Error') {
        const comp = this.#completeReads.get(tid);
        if (comp) {
          comp.queryFailed(
            new ArrowError(`Error performing db query: ${data.description}`, data)
          );
          this.#completeReads.delete(tid);
        }
        this.#reads.delete(tid);
      } else if (data.message === 'Success') {
        // Binary batches are completed by the end-of-stream marker in
        // handleBinaryMessage, not by the Success text message. Only
        // fail if no binary data was received at all.
        const readInfo = this.#reads.get(tid);
        if (readInfo && readInfo.buffer.length === 0 && this.#completeReads.get(tid)) {
          this.#completeReads
            .get(tid)
            ?.queryFailed(new Error('No data returned from db query'));
          this.#completeReads.delete(tid);
          this.#reads.delete(tid);
        }
      }
    });
    this.#ws.send(
      JSON.stringify({
        message: 'ReadDB',
        connection_name: connectionName,
        query,
        format: 'arrow',
        ...(params ? { params } : {}),
        trans_id: tid,
      })
    );
    return this.#withTimeout(tid, info.promise);
  }

  /**
   * Execute a SQL query against a database connection and return results as JSON.
   *
   * @param connectionName The name of the Database connection
   * @param query The SQL query to execute
   * @returns JSON string with query results
   */
  performDbQueryAsJson(
    connectionName: string,
    query: string,
    params?: TypedParam[]
  ): Promise<string> {
    if (!this.#ws || this.#ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('ArrowClient: WebSocket not connected'));
    }
    const tid = this.#transId++;
    const promise = new Promise<string>((resolve, reject) => {
      this.#pending.set(tid, (data) => {
        if (data.message === 'Error') {
          reject(new ArrowError(`Error performing db query as JSON: ${data.description}`, data));
        } else {
          resolve(data.results);
        }
      });
      this.#ws.send(
        JSON.stringify({
          message: 'ReadDB',
          connection_name: connectionName,
          query,
          format: 'json',
          ...(params ? { params } : {}),
          trans_id: tid,
        })
      );
    });
    return this.#withTimeout(tid, promise);
  }

  /**
   * Execute a SQL query and save results as a static table.
   *
   * @param tableName Name for the new static table
   * @param connectionName The name of the Database connection
   * @param query The SQL query to execute
   * @returns Table creation result
   */
  createTableFromQuery(
    tableName: string,
    connectionName: string,
    query: string,
    params?: TypedParam[]
  ): Promise<object> {
    if (!this.#ws || this.#ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('ArrowClient: WebSocket not connected'));
    }
    const tid = this.#transId++;
    return new Promise<object>((resolve, reject) => {
      this.#pending.set(tid, (data) => {
        if (data.message === 'Error') {
          reject(new ArrowError(`Error creating table from query: ${data.description}`, data));
        } else {
          resolve(data.results ? JSON.parse(data.results) : {});
        }
      });
      // Params are persisted with the table definition so newton can replay
      // the identical statement on refresh and rebuild (EHQ-115).
      const argStr = JSON.stringify({
        name: tableName,
        connection_name: connectionName,
        query,
        ...(params ? { params } : {}),
      });
      this.#ws.send(
        JSON.stringify({
          message: 'DoAction',
          action: 'STCreateFromQuery',
          argument: argStr,
          trans_id: tid,
        })
      );
    });
  }

  /**
   * List available actions on the server.
   *
   * @returns Array of action objects
   */
  listActions(): Promise<any[]> {
    if (!this.#ws || this.#ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('ArrowClient: WebSocket not connected'));
    }
    const tid = this.#transId++;
    return new Promise<any[]>((resolve, reject) => {
      this.#pending.set(tid, (response) => {
        if (response.message === 'Error') {
          reject(new ArrowError(`ArrowClient: Error listing actions: ${response.description}`, response));
          return;
        }
        resolve(response.actions || []);
      });
      this.#sendMessage({
        message: 'ListActions',
        trans_id: tid,
      }, tid);
    });
  }

  /**
   * List available flights matching optional criteria.
   *
   * @param criteria Optional filter criteria
   * @param binary Whether to return binary data
   * @returns Array of flight info objects
   */
  listFlights(criteria?: string, binary?: boolean): Promise<any[]> {
    if (!this.#ws || this.#ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('ArrowClient: WebSocket not connected'));
    }
    const tid = this.#transId++;
    return new Promise<any[]>((resolve, reject) => {
      this.#pending.set(tid, (response) => {
        if (response.message === 'Error') {
          reject(new ArrowError(`ArrowClient: Error listing flights: ${response.description}`, response));
          return;
        }
        resolve(response.flights || []);
      });
      this.#sendMessage({
        message: 'ListFlights',
        criteria,
        binary,
        trans_id: tid,
      }, tid);
    });
  }

  /**
   * Get detailed info about a specific flight.
   *
   * @param flight The flight descriptor name
   * @returns Flight info object
   */
  getFlightInfo(flight: string): Promise<any> {
    if (!this.#ws || this.#ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('ArrowClient: WebSocket not connected'));
    }
    const tid = this.#transId++;
    return new Promise<any>((resolve, reject) => {
      this.#pending.set(tid, (response) => {
        if (response.message === 'Error') {
          reject(new ArrowError(`ArrowClient: Error getting flight info: ${response.description}`, response));
          return;
        }
        resolve(response.info);
      });
      this.#sendMessage({
        message: 'GetInfo',
        flight,
        trans_id: tid,
      }, tid);
    });
  }

  /**
   * Create a table from a file in cloud storage.
   *
   * @param filename Path to the file in cloud storage
   * @param tableName Name for the new table
   * @param options Optional format, delimiter, and header settings
   * @returns Table creation result with tableName, rowCount, and schema
   */
  createTableFromFile(
    filename: string,
    tableName: string,
    options?: { format?: string; delimiter?: string; hasHeader?: boolean }
  ): Promise<{ tableName: string; rowCount: number; schema: string }> {
    if (!this.#ws || this.#ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('ArrowClient: WebSocket not connected'));
    }
    const tid = this.#transId++;
    return new Promise<{ tableName: string; rowCount: number; schema: string }>((resolve, reject) => {
      this.#pending.set(tid, (message) => {
        if (message.message === 'Error') {
          reject(new ArrowError(`ArrowClient: Error creating table from file: ${message.description}`, message));
          return;
        }
        if (message.message === 'TableCreated') {
          resolve({
            tableName: message.table_name,
            rowCount: message.row_count,
            schema: message.schema,
          });
        } else {
          reject(new Error(`ArrowClient: Unexpected response: ${message.message}`));
        }
      });

      const requestMessage: any = {
        message: 'CreateTableFromFile',
        filename,
        table_name: tableName,
        trans_id: tid,
      };
      if (options?.format) {
        requestMessage.format = options.format;
      }
      if (options?.delimiter !== undefined) {
        requestMessage.delimiter = options.delimiter;
      }
      if (options?.hasHeader !== undefined) {
        requestMessage.has_header = options.hasHeader;
      }

      this.#sendMessage(requestMessage, tid);
    });
  }

  // private methods

  /**
   * Send a JSON message to the server. If there are any error serializing the message,
   * the transactionId will be removed from the pending list.
   * @param message The message to serialize to JSON
   * @param tid The transactionId to remove from the pending list if the message fails to send
   */
  #sendMessage(message: object, tid?: number) {
    try {
      this.#ws.send(JSON.stringify(message));
    } catch (e) {
      if (tid) {
        this.#pending.delete(tid);
      }
      throw e;
    }
  }

  #massageBatchSize(batchSize: number) {
    if (batchSize < 1) {
      batchSize = DEFAULT_BATCH_SIZE;
    } else if (batchSize > MAX_BATCH_SIZE) {
      batchSize = MAX_BATCH_SIZE;
    }
    return batchSize;
  }

  #handleMessage(event: MessageEvent) {
    if (typeof event.data === 'string') {
      this.#handleJsonMessage(event.data);
    } else if (event.data instanceof Blob) {
      GeneralHelpers.blobToBuffer(event.data).then((buffer) => {
        // Convert Buffer to ArrayBuffer
        const arrayBuffer = new ArrayBuffer(buffer.length);
        const view = new Uint8Array(arrayBuffer);
        view.set(buffer);
        this.#handleBinaryMessage(arrayBuffer);
      });
    } else if (event.data instanceof ArrayBuffer) {
      this.#handleBinaryMessage(event.data);
    }
  }

  #handleBinaryMessage(data: ArrayBuffer) {
    const transIdBuffer = new Uint8Array(data.slice(0, 20));
    const transIdStr = Array.from(transIdBuffer)
      .map((i) => String.fromCharCode(i))
      .join('');
    const trans_id = parseInt(transIdStr, 10);
    const batchStr = Array.from(new Uint8Array(data.slice(20, 30)))
      .map((i) => String.fromCharCode(i))
      .join('');
    const batch_id = parseInt(batchStr, 10);
    const readInfo = this.#reads.get(trans_id);
    if (!readInfo) {
      console.log(
        `ArrowClient: Received binary data for an unknown transaction tid=${trans_id} batch=${batch_id}`
      );
      return;
    }
    const udata = new Uint8Array(data);
    if (udata.length === 30) {
      const comp = this.#completeReads.get(trans_id);
      if (comp) {
        try {
          if (comp.rawBytes) {
            // Return raw Arrow IPC bytes without re-serialization
            comp.querySuccess(new Uint8Array(readInfo.buffer));
          } else {
            // newton-rs sends one Arrow IPC stream per output
            // partition, concatenated into a single buffer. Decode
            // every stream and combine their record batches so we
            // don't silently drop all partitions after the first.
            const batches = [];
            let schema: Schema | undefined;
            for (const reader of RecordBatchReader.readAll(readInfo.buffer)) {
              schema ??= reader.schema;
              for (const batch of reader) {
                batches.push(batch);
              }
            }
            if (schema === undefined) {
              // No IPC stream decoded (empty or corrupt payload). Fail loudly
              // rather than returning an empty, schema-less table, which would
              // re-hide the data-loss class of bug this guards against.
              throw new Error(
                'no Arrow IPC stream could be decoded from the query result',
              );
            }
            // For a valid zero-row result (no batches) keep the decoded schema
            // so asTable consumers still see the columns, matching the old
            // tableFromIPC behavior.
            const table = batches.length > 0
              ? new Table(batches)
              : new Table(schema);
            if (comp.asTable) {
              comp.querySuccess(table as any);
            } else {
              const arrayBuffer: Uint8Array = tableToIPC(table, 'file');
              comp.querySuccess(arrayBuffer);
            }
          }
        } catch (e) {
          const newError = new Error("Parser: " + e);
          newError.cause = e;
          comp.queryFailed(newError);
        }
        this.#completeReads.delete(trans_id);
      }
      // this is the a flag that all the data has been read
      this.#reads.delete(trans_id);
      return;
    }
    const buf_data = udata.subarray(30);
    readInfo.buffer = Buffer.concat([readInfo.buffer, Buffer.from(buf_data)]);
    console.log(
      `ArrowClient: Received ${buf_data.length} bytes of binary data tid=${trans_id}`
    );
  }

  /**
   * Reject every in-flight request. Used when the server reports an error it
   * can't tie to a specific transaction id, so we cannot know which request
   * failed — failing them all surfaces the error instead of hanging forever.
   */
  #failAllPending(error: Error) {
    if (this.#currentPut) {
      try {
        this.#currentPut.errorHandler(error.message);
      } catch {
        // best-effort teardown
      }
      this.#currentPut = undefined;
    }
    // Reject the binary read promises first so the query callbacks below find
    // no completeReads entry and don't attempt to reject a second time.
    for (const comp of this.#completeReads.values()) {
      comp.queryFailed(error);
    }
    this.#completeReads.clear();
    this.#reads.clear();
    // Invoke the remaining pending callbacks with the error. Handlers that
    // gate a caller's promise reject when they see message === 'Error', so
    // this settles those requests. Internal fire-and-forget handlers (e.g.
    // the DSList refresh) don't back a caller promise and tolerate an
    // error-shaped message, so invoking and clearing them is harmless.
    const errorMessage = { message: 'Error', description: error.message };
    for (const cb of this.#pending.values()) {
      try {
        cb.call(this, errorMessage);
      } catch {
        // best-effort teardown
      }
    }
    this.#pending.clear();
  }

  /**
   * Wrap a pending request promise with a hard timeout so a lost or
   * unparseable response can never hang a run forever. On timeout the tracking
   * maps for `tid` are cleaned up and the returned promise rejects.
   */
  #withTimeout<T>(tid: number, promise: Promise<T>): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let resolveTimeout: ((value: T) => void) | undefined;
    const timeout = new Promise<T>((resolve, reject) => {
      resolveTimeout = resolve;
      timer = setTimeout(() => {
        this.#pending.delete(tid);
        this.#reads.delete(tid);
        this.#completeReads.delete(tid);
        reject(
          new Error(
            `ArrowClient: request timed out after ${REQUEST_TIMEOUT_MS}ms (tid=${tid})`
          )
        );
      }, REQUEST_TIMEOUT_MS);
    });
    return Promise.race([promise, timeout]).finally(() => {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
      // Settle the losing timeout promise so it (and the closure capturing
      // `this`/`tid`) can be garbage-collected instead of dangling pending.
      resolveTimeout?.(undefined as T);
    });
  }

  #handleJsonMessage(str: string) {
    const data = JSON.parse(str);
    if (!data.message) {
      console.log(`ArrowClient: Invalid message received: ${str}`);
      return;
    }
    // An error with no usable trans_id is a failure the server couldn't
    // attribute to a specific request (e.g. it failed before parsing the
    // ticket). trans_ids start at 1, so null/undefined/0 all mean "no usable
    // id" — it can't be routed to one pending promise, so fail every in-flight
    // request with the server's description rather than dropping the message,
    // which would leave the awaiting step hung forever.
    if (!data.trans_id) {
      if (data.message === 'Error') {
        this.#failAllPending(
          new ArrowError(
            `Arrow query failed: ${data.description ?? data.error ?? 'unknown error'}`,
            data
          )
        );
      } else {
        console.log(`ArrowClient: Invalid message received: ${str}`);
      }
      return;
    }
    const header = data as ResponseHeader;
    if (this.#currentPut && this.#currentPut.transId === header.trans_id) {
      this.#currentPut.handleResponse(data);
    } else if (this.#pending.has(header.trans_id)) {
      this.#pending.get(header.trans_id)?.call(this, data);
      this.#pending.delete(header.trans_id);
    } else {
      // manully handle the message
      switch (header.message) {
        case 'Connected':
          {
            this.#state = ArrowClientState.AWAIT_DS_LIST;
            const list_tid = this.#transId++;
            this.#pending.set(list_tid, (message) => {
              try {
                this.#handleDataSetList(message);
              } catch (e) {
                console.error('ArrowClient: Failed to parse dataset list', e);
              }
              this.#state = ArrowClientState.OPEN;
              this.#readySignal.set(true);
              this.#resolveConnected();
              this.#resolveConnected = () => { };
            });
            this.#ws.send(
              JSON.stringify({
                message: 'DoAction',
                action: 'DSList',
                argument: '',
                trans_id: list_tid,
              })
            );
          }
          break;
        case 'Error':
          console.error(`ArrowClient: Error message: ${data.description}${typeof data.code === 'string' && data.code ? ' [' + data.code + ']' : ''}`);
          break;
        case 'StartBinary':
        case 'Continue':
        case 'PutComplete':
          if (this.#currentPut) {
            this.#currentPut.handleResponse(data);
          } else {
            console.error(
              'ArrowClient: Put response received but no currentPut'
            );
            this.#currentPut = undefined;
          }
          break;
        case 'Success':
        case 'Disconnected':
          break;
        default:
          console.log(`ArrowClient: Unhandled message: ${header.message}`);
          break;
      }
    }
  }

  #handleDataSetList(data: any) {
    const map = new Map<string, DataSet>();
    const dsets = JSON.parse(data.results ?? '[]');
    for (const ds of dsets) {
      ds.partitionCols = JSON.parse(ds.partition_cols);
      map.set(ds.name, ds);
      if (ds.views) {
        this.#views.set(ds.name, ds.views);
      }
    }
    this.#dataSets.set(map);
  }
}
